import { useContext, useEffect, useState } from 'react';

import { Field, Image, RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { WildCardRouteContext } from 'src/context/WildCardRoute/WildCardRouteContext';

import { LearningTypes } from 'src/enums/LearningTypes';

import { SitecoreContextValue } from 'src/lib/ComponentProps';

import { parseLearningFormats } from 'src/mappers/fields/parseLearningFormats';

import { ContentDetailItem } from 'src/types/ContentDetailsItem';

import { FieldEditFrame } from 'src/utils/customFieldEditFrame';
import { getAccessTypeStringContent } from 'src/utils/getAccessTypeString';
import { getRcniResourceUrl } from 'src/utils/getRcniResourceUrl';
import { isResourceContentPage, isResourcePage } from 'src/utils/isTemplate';

import { Button } from 'src/components/basicContent/_Button/Button';
import { CourseFields } from 'src/components/courseOverview/CourseFields';
import { EditorHelp } from 'src/components/layout/_EditorHelp/_EditorHelp';

import AlertSvgIcon from 'public/icons/alert.svg';
import AwardSvgIcon from 'public/icons/award.svg';
import CalendarIconSvg from 'public/icons/calendar.svg';
import ClockSvgIcon from 'public/icons/clock.svg';
import GraduationCapSvg from 'public/icons/GraduationCap.svg';
import PeopleSvg from 'public/icons/People.svg';
import PersonTickSvgIcon from 'public/icons/PersonTick.svg';
import PriceTagSvg from 'public/icons/PriceTag.svg';

import { ContentOverviewSummaryFields } from '../ContentOverviewSummaryFields';

import styles from './ContentOverviewTile.module.css';

type ContentOverviewTileProps = {
	contentDetailItem: ContentDetailItem | null;
	RCNiArticleUrl?: Field<string>;
	fields: ContentOverviewSummaryFields & CourseFields;
};

type HeaderFields = {
	headerText?: Field<string>;
	subHeaderText?: Field<string>;
	headerIcon?: JSX.Element;
	index?: number;
};

export default function ContentOverviewTile(props: ContentOverviewTileProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const WildCardRoute = useContext(WildCardRouteContext);

	const [readTimeContent, setReadTimeContent] = useState('');

	useEffect(() => {
		const interval = setInterval(() => {
			const container = document.getElementById('readTime');
			if (!container) {
				return;
			}

			setReadTimeContent(container.innerHTML.split(' page')[0] || '');

			const obs = new MutationObserver(() => {
				setReadTimeContent(container.innerHTML.split(' page')[0] || '');
			});

			obs.observe(container, {
				childList: true,
			});

			return () => obs.disconnect();
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	const { text, id } = getAccessTypeStringContent({
		accessType: props.fields?.AccessType,
		openAccessText: props.fields?.OpenAccessText,
		registeredUserText: props.fields?.RegisteredUserText,
		rcnMemberText: props.fields?.RCNMemberText,
		organisationalProductText: props.fields?.OrganisationalProductText,
		rcniPlusSubscriberText: props.fields?.RCNiPlusSubscriberText,
	});

	let learningTypeField: Field<string> | undefined;
	let learningTypeDescriptionField: Field<string> | undefined;
	if (props.contentDetailItem?.LearningType == LearningTypes.Course) {
		learningTypeField = { value: sitecoreContext.dictionary.courseText };
		learningTypeDescriptionField = { value: sitecoreContext.dictionary.courseDescription };
	} else if (props.contentDetailItem?.LearningType == LearningTypes.Resource) {
		learningTypeField = { value: sitecoreContext.dictionary.resourceText };
		learningTypeDescriptionField = { value: sitecoreContext.dictionary.resourceDescription };
	}

	const portfolioUrl = getRcniResourceUrl(props.fields, props.contentDetailItem);

	const learningFormatList = parseLearningFormats(props.fields.LearningFormat);

	const templateId = WildCardRoute.wildCardItemInfo?.TemplateId ?? sitecoreContext.route.templateId;
	const hideReadTime = ((!isResourcePage(templateId) && !isResourceContentPage(templateId)) || props.fields.HideReadTime?.value) ?? true;

	const HeaderComponent = ({ headerText, subHeaderText, headerIcon }: HeaderFields) => {
		return (
			<div className={styles.headerContainer}>
				<span className={styles.header}>
					{headerIcon && <span>{headerIcon}</span>}
					<Text tag="h4" field={headerText} className={styles.headerText} />
				</span>
				<RichText tag="h5" field={subHeaderText} className={styles.subHeader} />
			</div>
		);
	};

	return (
		<section className={styles.contentOverviewContainer}>
			<HeaderComponent headerText={learningTypeField} subHeaderText={learningTypeDescriptionField} />

			<FieldEditFrame
				itemId={props.contentDetailItem?.Id ?? undefined}
				fields={[
					'CourseDuration',
					'CourseDurationDescription',
					'LearningFormat',
					'AccessType',
					'CPDHours',
					'AcademicCredits',
					'AcademicCreditsDescription',
					'RCNiPortfolioText',
				]}
				header="Edit content"
			>
				<EditorHelp text={'Click here to edit the summary'} />
				<div className={styles.bodyContainer}>
					{/* Costs */}
					{props.contentDetailItem?.IsLmsCourse && (
						<>
							<EditorHelp text={'Costs'} />
							{props.fields.items?.map((costItem, index) => (
								<HeaderComponent
									key={index}
									headerText={{
										value: `${costItem.fields?.Cost?.value} ${costItem.fields?.PlusVat?.value ? costItem.fields?.PlusVatText?.value : ''}`,
									}}
									subHeaderText={costItem.fields.CostDescription}
									headerIcon={<PriceTagSvg />}
								/>
							))}
						</>
					)}

					{/* Course Duration */}
					{props.contentDetailItem?.IsLmsCourse && props.fields.CourseDuration?.value && (
						<>
							<EditorHelp text={'Course Duration'} />
							<HeaderComponent
								headerText={props.fields.CourseDuration}
								subHeaderText={props.fields.CourseDurationDescription}
								headerIcon={<CalendarIconSvg />}
							/>
						</>
					)}

					{/* Learning Format */}
					{props.contentDetailItem?.IsLmsCourse ||
						props.contentDetailItem?.IsLmsModule ||
						(props.contentDetailItem?.IsCpdArticle && (
							<>
								<EditorHelp text={'Learning format'} />
								{learningFormatList?.map((learningFormat, index) => (
									<HeaderComponent
										key={index}
										headerText={{ value: learningFormat }}
										subHeaderText={props.fields.LearningFormatDescription}
										headerIcon={<PeopleSvg />}
									/>
								))}
							</>
						))}

					{/* Access Type */}
					{props.fields?.AccessType?.length > 0 && (
						<>
							<EditorHelp text={'Access type'} />
							<HeaderComponent
								headerText={{ value: text }}
								subHeaderText={props.fields?.AccessType?.find((filter) => filter.id === id)?.fields.Description}
								headerIcon={
									props.fields?.RcniLogo?.value && (props.contentDetailItem?.IsCpdArticle || props.contentDetailItem?.IsLmsModule) ? (
										<Image media={props.fields.RcniLogo} className={styles.rcniLogo} />
									) : (
										<PersonTickSvgIcon />
									)
								}
							/>
						</>
					)}

					{/* Academic Credits */}
					{props.contentDetailItem?.IsLmsCourse && props.fields.AcademicCredits?.value && (
						<>
							<EditorHelp text={'Credits'} />
							<HeaderComponent
								headerText={props.fields.AcademicCredits}
								subHeaderText={props.fields.AcademicCreditsDescription}
								headerIcon={<GraduationCapSvg />}
							/>
						</>
					)}

					{/* CPD Hours */}
					{(props.contentDetailItem?.IsLmsCourse || props.contentDetailItem?.IsRcniLearningModule) && props.contentDetailItem.CPDHours && (
						<>
							<EditorHelp text={'CPD Hours'} />
							<HeaderComponent
								headerText={{ value: props.contentDetailItem.CPDHours + ' ' + sitecoreContext.dictionary.cpdHoursText }}
								subHeaderText={{ value: sitecoreContext.dictionary.cpdHoursDescription }}
								headerIcon={<GraduationCapSvg />}
							/>
						</>
					)}

					{/* Read Time */}
					{!hideReadTime && (
						<>
							<EditorHelp text={'Read time'} />
							<HeaderComponent
								headerText={{
									value: sitecoreContext.dictionary.readingTimeText?.replace('X', readTimeContent),
								}}
								subHeaderText={{
									value: sitecoreContext.dictionary.readingTimeDescription?.replace('X', readTimeContent),
								}}
								headerIcon={<ClockSvgIcon />}
							/>
						</>
					)}

					{/* Content Advisory */}
					{props.contentDetailItem?.ShowAdvisoryContent && (
						<>
							<EditorHelp text={'Content Advisory'} />
							<HeaderComponent
								headerText={{
									value: sitecoreContext.dictionary.advisoryContent,
								}}
								subHeaderText={{ value: props.contentDetailItem?.AdvisoryContentText ?? '' }}
								headerIcon={<AlertSvgIcon />}
							/>
						</>
					)}

					{/* RCNi Portfolio */}
					{(props.contentDetailItem?.IsLmsModule || props.contentDetailItem?.IsCpdArticle) && (
						<>
							<EditorHelp text={'Portfolio link'} />
							<div className={`${styles.headerContainer} ${styles.headerContainerWithLink}`}>
								<a
									className={`${styles.header} link linkHoverWeighted linkOffset`}
									title={sitecoreContext.dictionary.rcniPortfolioText}
									href={portfolioUrl}
									target="_blank"
									rel="noreferrer"
								>
									<AwardSvgIcon />
									<Text field={{ value: sitecoreContext.dictionary.rcniPortfolioText }} />
								</a>
							</div>
						</>
					)}
				</div>

				<div className={styles.footerContainer}>
					<Button
						className={`${styles.viewResourceLink} link linkHoverWeighted linkOffset`}
						title={
							props.contentDetailItem?.LearningType == LearningTypes.Course
								? sitecoreContext.dictionary.viewCourseContents
								: sitecoreContext.dictionary.viewResourceContents
						}
						onClick={() => {
							const elementId = props.contentDetailItem?.IsRcniLearningModule
								? 'learning-module-information'
								: 'resource-navigation';

							const element = [...document.querySelectorAll(`#${elementId}`)]
								.find(el => !el.closest('#download-options-modal'));

							if (element) {
								element.scrollIntoView({ behavior: 'smooth' });
							} else {
								document
									.getElementById('audience')
									?.scrollIntoView({ behavior: 'smooth' });
							}
						}}
					>
						<Text
							field={{
								value:
									props.contentDetailItem?.LearningType == LearningTypes.Course
										? sitecoreContext.dictionary.viewCourseContents
										: sitecoreContext.dictionary.viewResourceContents,
							}}
						/>
					</Button>
				</div>
			</FieldEditFrame>
		</section>
	);
}
